import React, { useCallback, useState } from 'react';

import {
  getIsLargeRun,
  getRunAnswerCount,
  LARGE_RUN_ANSWER_COUNT,
} from '@aline/common/question-plan';
import {
  ConfirmationDialog,
  ConfirmationDialogVariant,
} from 'src/components/generic/ConfirmationDialog';
import { makeStyles } from 'tss-react/mui';

import { formatCount } from './question-plan-helpers';
import type { QuestionPlanRunCounts } from './question-plan-helpers';
import { CardPrimaryAction, CardSecondaryAction } from '../PendingInteractionCardChrome';

const UNCOUNTED_RUN_MESSAGE = `Aline could not count the documents these filters match, so this run may extract more than the ${LARGE_RUN_ANSWER_COUNT.toLocaleString()} answers a run can start without asking. It can take a while and uses AI tokens from your organization's allowance.`;

const useStyles = makeStyles()({
  // Pushes the other actions right, so the alternative sits apart from Skip and the primary.
  leftAction: {
    marginRight: 'auto',
  },
});

export interface QuestionPlanLeftAction {
  label: string;
  onClick: () => void;
}

interface QuestionPlanActionsProps {
  counts: QuestionPlanRunCounts;
  disabled: boolean;
  onRun: (options: { largeRunConfirmed: boolean }) => void;
  onSkip: () => void;
  primaryLabel: string;
  /** The document count did not come back, so the run might be large and always asks first. */
  countFailed?: boolean;
  /** An alternative to running, on the left: "Create only" on a report plan. */
  leftAction?: QuestionPlanLeftAction;
  primaryDisabledReason?: string;
}

/**
 * The footer actions of a question plan card: an optional alternative on the
 * left, then Skip and the run. A run past {@link LARGE_RUN_ANSWER_COUNT}
 * answers asks again in a dialog, because a line of warning on the card is
 * easy to read past. The dialog's other button is the alternative when there
 * is one, so the user can still create without running.
 */
export const QuestionPlanActions = React.memo(function QuestionPlanActions({
  countFailed = false,
  counts,
  disabled,
  leftAction,
  onRun,
  onSkip,
  primaryDisabledReason,
  primaryLabel,
}: QuestionPlanActionsProps) {
  const { classes } = useStyles();
  const [confirming, setConfirming] = useState(false);
  const answerCount = getRunAnswerCount(counts);

  const handlePrimary = useCallback(() => {
    if (countFailed || getIsLargeRun(answerCount)) setConfirming(true);
    else onRun({ largeRunConfirmed: false });
  }, [answerCount, countFailed, onRun]);

  const handleConfirm = useCallback(() => {
    setConfirming(false);
    onRun({ largeRunConfirmed: true });
  }, [onRun]);

  const handleDialogSecondary = useCallback(() => {
    setConfirming(false);
    leftAction?.onClick();
  }, [leftAction]);

  return (
    <>
      {leftAction && (
        <div className={classes.leftAction}>
          <CardSecondaryAction disabled={disabled} onClick={leftAction.onClick}>
            {leftAction.label}
          </CardSecondaryAction>
        </div>
      )}
      <CardSecondaryAction disabled={disabled} onClick={onSkip}>
        Skip
      </CardSecondaryAction>
      <CardPrimaryAction
        disabled={disabled}
        disabledReason={primaryDisabledReason}
        onClick={handlePrimary}
      >
        {primaryLabel}
      </CardPrimaryAction>
      <ConfirmationDialog
        message={countFailed ? UNCOUNTED_RUN_MESSAGE : getLargeRunMessage(counts, answerCount)}
        open={confirming}
        primaryText={countFailed ? 'Run anyway' : `Run ${answerCount.toLocaleString()} answers`}
        secondaryText={leftAction?.label ?? 'Cancel'}
        title="Run a large extraction?"
        variant={ConfirmationDialogVariant.Warn}
        onClickPrimary={handleConfirm}
        onClickSecondary={handleDialogSecondary}
        onClose={() => setConfirming(false)}
      />
    </>
  );
});

function getLargeRunMessage(counts: QuestionPlanRunCounts, answerCount: number) {
  const documents = formatCount(counts.documentCount, 'document');
  const questions = formatCount(counts.questionCount, 'question');
  return `This extracts ${answerCount.toLocaleString()} answers (${documents} × ${questions}), more than the ${LARGE_RUN_ANSWER_COUNT.toLocaleString()} a run can start without asking. It can take a while and uses AI tokens from your organization's allowance.`;
}
